import { ChangeEvent, useEffect, useRef, useState } from "react";
import Popover from "../popover/popover";
import ViewersTagsList from "./viewersTagsList";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { ConditionName } from "../../../../../interfaces";
import { conditions } from "../../../../../helpers";
import { setIsMovieFilter, setIsSeriesFilter, setViewersTagsList } from "../../../../../redux/slices/tagsSlice";
import { handleClearTagInput, handleClickTag, handleCloseTagPopover, handleTagInput, handleViewersTagList } from "../../../../../redux/actions/tags.actions";
import { shallowEqual } from 'react-redux';
import dynamic from "next/dynamic";

const SelectedTagVideoList = dynamic(() => import("./selectedTagsVideoList"));
const CircleSpinner = dynamic(() => import("../spinners/circleSpinner"));
const Toggle = dynamic(() => import("../toggle/toggle"));
const TagsFilter = dynamic(() => import("./tagsFilter"));
const ArrowLeftIcon = dynamic(() => import("../../icon-components/arrowLeft"));
const TagsEmptyState = dynamic(() => import("../../icon-components/tagsEmptyState"));
const CloseIcon = dynamic(() => import("../../icon-components/closeIcon"));

const TagsPopover = () => {
    const [videoCount, setVideoCount] = useState<number>(0);
    const [listScrolled, setListScrolled] = useState<any[]>([]);
    const [tagInputLoading, setTagInputLoading] = useState<boolean>(false);
    const [loadingGetTags, setLoadingGetTags] = useState<boolean>(true);
    const { selectedTags, inputTagSearch, viewersTagsList, isMovieFilter, isSeriesFilter } = useAppSelector(({ tags: { selectedTags, inputTagSearch, viewersTagsList, isMovieFilter, isSeriesFilter } }) => ({ selectedTags, inputTagSearch, viewersTagsList, isMovieFilter, isSeriesFilter }), shallowEqual);
    const { tagsPopover } = useAppSelector(({ popovers: { tagsPopover } }) => ({ tagsPopover }), shallowEqual);

    const tagInputTimeoutRef = useRef<any>(null)

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (conditions(ConditionName.showTagsPopover) && conditions(ConditionName.isTouchScreen)) handleViewersTagList();
    }, [selectedTags.length])

    useEffect(() => {
        if (conditions(ConditionName.showTagsPopover)) {
            handleClickTag(setVideoCount, listScrolled, setListScrolled, setLoadingGetTags);
            dispatch(setViewersTagsList(viewersTagsList.filter((tag: string) => tag !== selectedTags[selectedTags.length - 1])))
        } else setLoadingGetTags(true);
    }, [tagsPopover, selectedTags.length, isMovieFilter, isSeriesFilter]);

    const customHeader = conditions(ConditionName.isTouchScreen) ? (
        <div className="pl-flex pl-items-center pl-justify-between pl-gap-2 pl-py-[19px] pl-px-4 pl-bg-gray-highLight dark:pl-bg-z-gray-700">
            <div className="pl-flex-none pl-w-[22px]">{tagInputLoading ? <CircleSpinner className="pl-w-[22px] pl-h-[22px] pl-text-[#77787B] dark:pl-text-white" /> : conditions(ConditionName.hasInputTagSearch) && <CloseIcon className="pl-w-6 pl-h-6 pl-stroke-z-z-gray-500 dark:pl-stroke-dark-5" onClick={() => handleClearTagInput(setVideoCount, listScrolled, setListScrolled)} />}</div>
            <input
                placeholder="جستجو"
                value={inputTagSearch}
                className="pl-flex-1 pl-bg-transparent pl-outline-none"
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleTagInput(event.target.value, setTagInputLoading, tagInputTimeoutRef, setVideoCount, listScrolled, setListScrolled, setLoadingGetTags)}
            />
            <ArrowLeftIcon className="pl-fill-z-gray-700 dark:pl-fill-gray-highLight" onClick={handleCloseTagPopover} />
        </div>
    ) : (
        <div className="pl-flex pl-items-center pl-justify-between pl-gap-2 pl-bg-gray-highLight pl-p-4 dark:pl-bg-z-gray-700">
            <div className="pl-relative pl-w-full pl-flex pl-items-center pl-justify-between pl-gap-2 pl-px-4 pl-border pl-border-z-secondary-500 pl-rounded-[10px] pl-bg-gray-highLight dark:pl-bg-z-gray-700">
                <input
                    autoFocus={true}
                    placeholder="جستجو"
                    value={inputTagSearch}
                    className="pl-flex-1 pl-bg-transparent pl-outline-none pl-text-[14px] pl-font-normal"
                    onChange={(event: ChangeEvent<HTMLInputElement>) => handleTagInput(event.target.value, setTagInputLoading, tagInputTimeoutRef, setVideoCount, listScrolled, setListScrolled, setLoadingGetTags)}
                />
                <div className="pl-flex-none pl-w-[22px]">{tagInputLoading ? <CircleSpinner className="pl-!w-[22px] pl-!h-[22px] pl-!text-[#77787B] dark:pl-!text-white" /> : conditions(ConditionName.hasInputTagSearch) && <CloseIcon className="pl-w-[22px] pl-h-[22px] pl-stroke-z-gray-500 dark:pl-stroke-dark-5 pl-cursor-pointer" onClick={() => handleClearTagInput(setVideoCount, listScrolled, setListScrolled)} />}</div>
                <div className="pl-flex pl-items-center pl-border-r-[1px] pl-!border-z-secondary-500 pl-py-1 pl-pr-4">
                    <TagsFilter />
                </div>
            </div>
            <CloseIcon className="pl-w-6 pl-h-6 pl-stroke-z-gray-500 dark:pl-stroke-dark-5 pl-cursor-pointer" onClick={handleCloseTagPopover} />
        </div>
    )

    return (        
        <Popover
            classNames={`${!conditions(ConditionName.isTouchScreen) && "!pl-bg-white dark:!pl-bg-dark-1 !pl-h-screen !pl-w-screen"}`}
            status={tagsPopover}
            onClose={handleCloseTagPopover}
            ref={undefined}
            isFullHeight={conditions(ConditionName.isComplexPortrait)}
            customHeader={customHeader}
            isHeader={true}
            type={conditions(ConditionName.isComplexPortrait) ? "popover" : "modal"}
            isHasCatchUpLineOnTop={conditions(ConditionName.isComplexPortrait)}
        >
            <div className={`${loadingGetTags ? "pl-overflow-hidden" : "pl-overflow-x-hidden"}`}>
                <ViewersTagsList />
                {conditions(ConditionName.isTouchScreen) && conditions(ConditionName.hasMovieDetails) && (
                    <div className="pl-flex pl-flex-col pl-gap-4 pl-mx-4 pl-border-y pl-border-secondary pl-pt-4 pl-pb-1 pl-mb-4">
                        <p className="pl-text-[15px] pl-font-bold">نوع محتوا</p>
                        <div className="pl-flex pl-justify-between pl-items-center">
                            <p className="pl-text-[14px] pl-font-normal">فیلم سینمایی</p>
                            <Toggle enabled={isMovieFilter} setEnabled={() => dispatch(setIsMovieFilter(!isMovieFilter))} />
                        </div>
                        <div className="pl-flex pl-justify-between pl-items-center">
                            <p className="pl-text-[14px] pl-font-norma">سریال</p>
                            <Toggle enabled={isSeriesFilter} setEnabled={() => dispatch(setIsSeriesFilter(!isSeriesFilter))} />
                        </div>
                    </div>
                )}
                {loadingGetTags ? (
                    <CircleSpinner className="pl-mx-auto" />
                ) : (
                    <>
                        {videoCount ? <SelectedTagVideoList listScrolled={listScrolled} setListScrolled={setListScrolled} videoCount={videoCount} /> : (
                            <div className="pl-w-full pl-h-full pl-flex pl-flex-col pl-justify-center pl-items-center pl-gap-8 pl-overflow-hidden">
                                <TagsEmptyState className={`${conditions(ConditionName.isTouchScreen) && "!pl-w-[200px] !pl-h-[200px]"}`} />
                                <p>باتوجه به تگ مورد نظر ویدیویی یافت نشد!</p>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Popover>
    )
}

export default TagsPopover;